import format from "date-fns/format"
import { COLOR_GRAY } from "../../../constants"
import type { ReportedHourViewModel } from "../../../hour-reports/api"
import { convertDecimalHoursToHoursAndMinutes } from "../../../hour-reports/utils"
import { escapeHtml } from "../../utils"
import { TreeGridApprovedHour } from "./types"
import { makeActionButtonHtml, makeStatusTip } from "./utils"

export const makeTreeGridApprovedHourRow = ({
  report,
  dateFormat,
  translations,
}: MakeTreeGridApprovedHourRowProps): TreeGridApprovedHour => {
  const projectOrTask = makeProjectOrTask(report, translations.notSelected)
  const durationInHoursAndMinutes = convertDecimalHoursToHoursAndMinutes(report.hours)
  const formattedDate = format(new Date(report.date), dateFormat)
  const isHourReportAdmin = report.editableAs === "hour-report-admin"
  const readyForCheckCanEdit = isHourReportAdmin || report.isReadyForCheckEnabled ? 1 : 0
  const readyForApprovalCanEdit = isHourReportAdmin || report.isReadyForApprovalEnabled ? 1 : 0
  const approvedCanEdit = isHourReportAdmin || report.isApprovedEnabled ? 1 : 0

  return {
    id: report.id,
    Def: "ApprovedHour",
    editableAs: report.editableAs,
    actions: makeReportActionsHtml(report, translations),
    status: report.status,
    reporterUserId: report.reporterUserId,
    reporterUserFullName: report.reporterUserFullName,
    date: formattedDate,
    projectOrTask: projectOrTask.html,
    projectOrTaskSearchValue: projectOrTask.searchValue,
    projectOrTaskExportValue: projectOrTask.exportValue,
    description: report.description || "-",
    worker: report.memberFullName || report.reporterUserFullName || "-",
    started: report.startTime || "-",
    finished: report.endTime || "-",
    hours: `${durationInHoursAndMinutes.hours}h ${durationInHoursAndMinutes.minutes}m`,
    hoursClass: "",
    hoursFormula: "",
    readyForCheck: report.isReadyForCheck ? 1 : 0,
    readyForCheckCanEdit,
    readyForCheckCanFocus: readyForCheckCanEdit,
    readyForCheckTip: makeStatusTip({
      action: translations.reported,
      person: report.reportedBy,
      date: report.createdAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    readyForApproval: report.isReadyForApproval ? 1 : 0,
    readyForApprovalCanEdit,
    readyForApprovalCanFocus: readyForApprovalCanEdit,
    readyForApprovalTip: makeStatusTip({
      action: translations.checked,
      person: report.checkedBy,
      date: report.checkedAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    approved: report.isApproved ? 1 : 0,
    approvedCanEdit,
    approvedCanFocus: approvedCanEdit,
    approvedTip: makeStatusTip({
      action: translations.approved,
      person: report.approvedBy,
      date: report.approvedAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    durationInDecimalHours: report.hours,
    Height: 54,
    MaxHeight: 72,
  }
}

const makeReportActionsHtml = (report: ReportedHourViewModel, translations: ApprovedHourRowTranslations) => {
  if (!report.editableAs) {
    return `<span class="approved-hours-actions approved-hours-actions--report"></span>`
  }

  return `
    <span class="approved-hours-actions approved-hours-actions--report">
      ${makeActionButtonHtml({
        action: "edit",
        label: translations.edit,
        icon: "edit",
      })}
      ${makeActionButtonHtml({
        action: "delete",
        label: translations.delete,
        icon: "delete",
      })}
    </span>
  `
}

const makeProjectOrTask = (report: ReportedHourViewModel, notSelected: string) => {
  if (report.task) return makeHourReportTask(report, notSelected)
  if (report.project) return makeHourReportProject(report, notSelected)

  return makeNotSelectedProjectOrTask(notSelected)
}

const makeHourReportTask = (report: ReportedHourViewModel, notSelected: string) => {
  const title = report.task?.title || notSelected
  const pathParts = getHourReportTaskPathParts(report)

  return {
    html: makeHourReportTaskHtml({ pathParts, title }),
    searchValue: [...pathParts, title].filter(Boolean).join(" "),
    exportValue: [...pathParts, title].filter(Boolean).join(" / "),
  }
}

const makeHourReportProject = (report: ReportedHourViewModel, notSelected: string) => {
  const title = report.project ? `${report.project.projectNumber} ${report.project.title}` : notSelected

  return {
    html: makeHourReportProjectHtml(title),
    searchValue: title,
    exportValue: title,
  }
}

const makeNotSelectedProjectOrTask = (title: string) => {
  return {
    html: makeHourReportProjectHtml(title),
    searchValue: title,
    exportValue: title,
  }
}

const getHourReportTaskPathParts = (report: ReportedHourViewModel) => {
  if (!report.project || !report.task?.taskNumber) return []

  return [
    report.task.taskNumber,
    report.project.title,
    ...(report.task.ancestors || []).map((ancestor) => ancestor.title),
  ]
}

const makeHourReportTaskHtml = ({ pathParts, title }: { pathParts: string[]; title: string }) => {
  const pathHtml = pathParts.length
    ? `<div style="line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${makeHourReportTaskPathHtml(
        pathParts
      )}</div>`
    : ""

  return `${pathHtml}${makeHourReportTaskTitleHtml(title)}`
}

const makeHourReportTaskTitleHtml = (title: string) => {
  return `<div style="line-height:1.4;">${escapeHtml(title)}</div>`
}

const makeHourReportProjectHtml = (title: string) => {
  return `<div style="line-height:1.4;">${escapeHtml(title)}</div>`
}

const makeHourReportTaskPathHtml = (pathParts: string[]) => {
  const [projectOrTaskNumber, projectTitle, ...ancestorTitles] = pathParts
  const spanStyles = `color:${COLOR_GRAY};cursor:pointer;font-size:0.625rem;font-weight:400;overflow:hidden;`
  const numberHtml = `<span style="${spanStyles}">${escapeHtml(projectOrTaskNumber)}</span>`
  const hasAncestorTasks = ancestorTitles.length > 0
  const projectDivider = hasAncestorTasks ? " / " : ""
  const projectHtml = `<span style="${spanStyles}">&nbsp;${escapeHtml(projectTitle)} ${projectDivider}</span>`
  const ancestorHtml = ancestorTitles
    .map((ancestorTitle, index) => {
      const isLast = index === ancestorTitles.length - 1
      const divider = isLast ? "" : " / "

      return `<span style="${spanStyles}">${escapeHtml(ancestorTitle)} ${divider}</span>`
    })
    .join("")

  return `${numberHtml}${projectHtml}${ancestorHtml}`
}

type MakeTreeGridApprovedHourRowProps = {
  report: ReportedHourViewModel
  dateFormat: string
  translations: ApprovedHourRowTranslations
}

export type ApprovedHourRowTranslations = {
  notSelected: string
  edit: string
  delete: string
  reported: string
  checked: string
  approved: string
  statusTip: (values: { action: string; person: string; date: string }) => string
}
